import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useChartTitle } from './ChartTitleContext';

export interface ChartNameSource {
    /** Category labels: the slice names of a pie or doughnut. */
    labels?: ReadonlyArray<unknown>;
    datasets?: ReadonlyArray<{ label?: string }>;
}

function distinctNames(values: ReadonlyArray<unknown>): string[] {
    const names = values
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => value !== '');
    return [...new Set(names)];
}

/**
 * The series a chart shows, for its accessible name: the dataset labels of a line or bar
 * chart, or the slice labels of a pie or doughnut, which has one unlabelled dataset.
 */
export function chartSeriesNames(data: ChartNameSource, kind: 'series' | 'slices'): string[] {
    if (kind === 'slices') return distinctNames(data.labels ?? []);
    return distinctNames((data.datasets ?? []).map((dataset) => dataset.label));
}

/**
 * The accessible name of a chart canvas. A canvas is a picture to a screen reader, so it is
 * named after what the reader would otherwise have to see: the chart's title and its series.
 */
export function chartAccessibleName(
    t: TFunction,
    title: string | undefined,
    series: ReadonlyArray<string>
): string {
    const trimmedTitle = title?.trim() ?? '';
    const seriesText = series.join(', ');

    if (trimmedTitle !== '' && seriesText !== '') {
        return t('atoms.chart.accessibleName.titleAndSeries', {
            title: trimmedTitle,
            series: seriesText,
        });
    }
    if (trimmedTitle !== '') {
        return t('atoms.chart.accessibleName.title', { title: trimmedTitle });
    }
    if (seriesText !== '') {
        return t('atoms.chart.accessibleName.series', { series: seriesText });
    }
    return t('atoms.chart.accessibleName.untitled');
}

/**
 * The accessible name for a chart wrapper's canvas. `title` wins over the title of the card
 * the chart sits in, for a chart whose own heading is not its card's.
 */
export function useChartAccessibleName(
    title: string | undefined,
    data: ChartNameSource,
    kind: 'series' | 'slices'
): string {
    const { t } = useTranslation();
    const cardTitle = useChartTitle();
    return chartAccessibleName(t, title ?? cardTitle, chartSeriesNames(data, kind));
}
